import { ONBOARDING_FORM_STYLES } from "@/features/onboarding/components/onboarding-form-styles";
import { AlertCircle } from "lucide-react";
import { useState } from "react";

import { Alert } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Loading from "@/components/Loading";

export function CredentialUploadMissingDialog({
  onContinue,
  onBack,
  missingDocuments,
  pendingReviewCount = 0,
  blockedUploadCount = 0,
}: {
  onContinue: () => void | Promise<void>;
  onBack: () => void;
  pendingReviewCount?: number;
  blockedUploadCount?: number;
  missingDocuments: {
    name: string;
  }[];
}) {
  let continueLabel = "Continue without uploading";
  let backLabel = "Upload remaining documents";
  let title = "Missing Documents";
  if (pendingReviewCount > 0) {
    continueLabel = "Continue and review later";
    backLabel = "Review documents";
    title = "Review Your Documents";
  }
  if (blockedUploadCount > 0) {
    continueLabel = "Continue for now";
    backLabel = "Check uploads";
    title = "Documents Need Attention";
  }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onContinue();
    } catch {
      setError("Could not continue. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onBack();
        }
      }}
    >
      <DialogContent
        className={`${ONBOARDING_FORM_STYLES.confirmation} sm:max-w-xl`}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Review outstanding documents before continuing onboarding.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto max-h-[calc(80vh-200px)]">
          {blockedUploadCount > 0 && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              {blockedUploadCount} uploaded{" "}
              {blockedUploadCount === 1 ? "file is" : "files are"} blocked by a
              security check and cannot be previewed or used. Upload another
              copy, or remove{" "}
              {blockedUploadCount === 1
                ? "the blocked file"
                : "the blocked files"}
              .
            </p>
          )}
          {pendingReviewCount > 0 && (
            <p className="rounded-xl border border-primary/15 bg-primary/5 p-4 text-primary">
              {pendingReviewCount} uploaded{" "}
              {pendingReviewCount === 1
                ? "file still needs"
                : "files still need"}{" "}
              review. Review and save the details to add them to your
              credentials. You can return to your profile to finish later.
            </p>
          )}
          {missingDocuments.length > 0 && (
            <>
              <p className="text-sm leading-6 text-slate-700">
                The following documents are not yet saved to your credentials:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {missingDocuments.map((doc) => (
                  <li
                    key={doc.name}
                    className="break-words text-sm leading-6 text-slate-600"
                  >
                    {doc.name}
                  </li>
                ))}
              </ul>
              <Alert>
                <AlertCircle className="h-5 w-5 shrink-0 float-left mr-2 mt-0.5" />
                <div>
                  <p className="font-semibold">
                    Important. Without these files you will be missing out on:
                  </p>
                  <ul className="list-disc pl-5 mt-1">
                    <li>Notifications when these credentials expire</li>
                    <li>Getting credentialed at all our facilities</li>
                  </ul>
                  <p className="mt-1">
                    You can always upload these documents later in your profile.
                  </p>
                </div>
              </Alert>
            </>
          )}
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="mt-2 flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-col sm:space-x-0 lg:flex-col">
          <Button
            className={`${ONBOARDING_FORM_STYLES.primaryButton} sm:w-full`}
            onClick={onBack}
            disabled={isLoading}
          >
            {backLabel}
          </Button>
          <Button
            className={`${ONBOARDING_FORM_STYLES.secondaryButton} sm:w-full`}
            variant="outline"
            onClick={handleContinue}
            disabled={isLoading}
          >
            {isLoading ? <Loading className="w-4 h-4" /> : continueLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
